/**
 * Resolve a banner click-through destination against the current origin.
 *
 * A same-origin destination (a bare path like "/rent/reporting/home", or a
 * fully-qualified URL on this host) is an in-app route the app should navigate
 * to in the same tab. Anything else, including a malformed URL, is external and
 * opens in a new tab.
 *
 * Shared by PropertyBanner and BannerCarousel so the two cannot drift.
 */
export const resolveBannerHref = (
  link?: string | null
): { href: string; internalPath: string | null } => {
  const href = link ? link.trim() : "";
  if (!href) return { href: "", internalPath: null };

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      return { href, internalPath: url.pathname + url.search + url.hash };
    }
  } catch {
    // Malformed URL → treat as external.
  }

  return { href, internalPath: null };
};

/**
 * True when a click should be handled by the router rather than the browser.
 * Modified clicks (new tab/window) and non-left clicks fall through to default
 * browser handling.
 */
export const isPlainLeftClick = (e: {
  defaultPrevented: boolean;
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean =>
  !e.defaultPrevented &&
  e.button === 0 &&
  !e.metaKey &&
  !e.ctrlKey &&
  !e.shiftKey &&
  !e.altKey;
